import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AlertService } from 'app/services/alert.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { SquareProductSalesService } from '../services/square-product-sales.service';
import * as SquareProductSalesActions from './square-product-sales.actions';

@Injectable()
export class SquareProductSalesEffects {
    private _actions$ = inject(Actions);
    private _squareProductSalesService = inject(SquareProductSalesService);
    private _alertService = inject(AlertService);

    messages = {
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    getAllSquareProductSales$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareProductSalesActions.loadSquareProductSales),
            switchMap((action) =>
                this._squareProductSalesService.getAll(action.params).pipe(
                    map((res) =>
                        SquareProductSalesActions.loadSquareProductSalesSuccess({
                            squareProductSales: res.data,
                            totalCount: res.pagination?.total ?? 0,
                        })
                    ),
                    catchError((error) =>
                        of(
                            SquareProductSalesActions.loadSquareProductSalesFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    getSquareProductSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareProductSalesActions.loadSquareProductSale),
            switchMap((action) =>
                this._squareProductSalesService.getOne(action.id).pipe(
                    map((res) =>
                        SquareProductSalesActions.loadSquareProductSaleSuccess({
                            squareProductSale: res.data,
                        })
                    ),
                    catchError((error) =>
                        of(
                            SquareProductSalesActions.loadSquareProductSaleFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    createSquareProductSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareProductSalesActions.createSquareProductSale),
            switchMap((action) =>
                this._squareProductSalesService.create(action.data).pipe(
                    map((res) =>
                        SquareProductSalesActions.createSquareProductSaleSuccess({
                            squareProductSale: res.data,
                            success: 'Square product sale created successfully',
                        })
                    ),
                    catchError((error) =>
                        of(
                            SquareProductSalesActions.createSquareProductSaleFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    updateSquareProductSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareProductSalesActions.updateSquareProductSale),
            switchMap((action) =>
                this._squareProductSalesService.update(action.data, action.id).pipe(
                    map((res) =>
                        SquareProductSalesActions.updateSquareProductSaleSuccess({
                            squareProductSale: res.data,
                            success: 'Square product sale updated successfully',
                        })
                    ),
                    catchError((error) =>
                        of(
                            SquareProductSalesActions.updateSquareProductSaleFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    deleteSquareProductSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareProductSalesActions.deleteSquareProductSale),
            switchMap((action) =>
                this._squareProductSalesService.delete(action.id).pipe(
                    map(() =>
                        SquareProductSalesActions.deleteSquareProductSaleSuccess({
                            id: action.id,
                            success: 'Square product sale deleted successfully',
                        })
                    ),
                    catchError((error) =>
                        of(
                            SquareProductSalesActions.deleteSquareProductSaleFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    // -----------------------------------------------------------
    // @ Side Effects (alerts)
    // -----------------------------------------------------------

    onSquareProductSalesActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    SquareProductSalesActions.createSquareProductSaleSuccess,
                    SquareProductSalesActions.updateSquareProductSaleSuccess,
                    SquareProductSalesActions.deleteSquareProductSaleSuccess
                ),
                tap((action) =>
                    this._alertService.showAlert(action.success, 'success')
                )
            ),
        { dispatch: false }
    );

    onSquareProductSalesActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    SquareProductSalesActions.loadSquareProductSalesFailure,
                    SquareProductSalesActions.loadSquareProductSaleFailure,
                    SquareProductSalesActions.createSquareProductSaleFailure,
                    SquareProductSalesActions.updateSquareProductSaleFailure,
                    SquareProductSalesActions.deleteSquareProductSaleFailure
                ),
                tap((action) =>
                    this._alertService.showAlert(action.error, 'error')
                )
            ),
        { dispatch: false }
    );
}
